// pipe: 4B.8 (Task 12) — driver pickup-execution container. Wires the
// Bearer-authed DriverErpService (via the driver context hook) to the pure
// PickupExecView. ALL the fetch/state/effect + the geolocation consent call + the
// PickupCompleteBody construction + navigation live HERE, never in
// pickup-exec-view.tsx (the Vitest-tested seam).
//
// THE LOAD-BEARING LOGIC (the brief): the body's `geo` is set ONLY when coords
// were obtained (omitted on a denial — GPS is exceeds-standard, so a denial does
// not block completion); `net_weight_override_reason` is set ONLY when the entered
// net weight deviates from the declared weight; `client_op_id` is a fresh UUID per
// attempt (crypto.randomUUID()). Complete routes through the offline outbox (Task
// 13): online flushes immediately, offline queues + replays on reconnect.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { getPosition } from '../native/geolocation';
import { tapHaptic } from '../native/haptics';
import { useOutbox } from '../offline/outbox-provider';
import type { PickupDetail } from '../services/driver-erp';
import { useDriverErp } from '../services/driver-erp-provider';
import { asNumber, buildPickupCompleteBody } from './pickup-body';
import type { GpsState } from './pickup-exec-view';
import { PickupExecView } from './pickup-exec-view';

export function PickupExec() {
  const erp = useDriverErp();
  const outbox = useOutbox();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const [detail, setDetail] = useState<PickupDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // The three §3.2.43 signatures (PNG data-URLs, '' when not yet captured).
  const [releasingSig, setReleasingSig] = useState('');
  const [agentSig, setAgentSig] = useState('');
  const [logisticsSig, setLogisticsSig] = useState('');
  const [agentEmployeeId, setAgentEmployeeId] = useState('');
  const [netWeight, setNetWeight] = useState('');
  const [overrideReason, setOverrideReason] = useState('');
  const [gpsState, setGpsState] = useState<GpsState>('pending');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) {
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    erp
      .pickupDetail(id)
      .then(d => {
        if (!cancelled) {
          setDetail(d);
        }
      })
      .catch((e: unknown) => {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : String(e));
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [erp, id]);

  const onSig = useCallback(
    (role: 'releasing' | 'agent' | 'logistics', dataUrl: string) => {
      if (role === 'releasing') {
        setReleasingSig(dataUrl);
      } else if (role === 'agent') {
        setAgentSig(dataUrl);
      } else {
        setLogisticsSig(dataUrl);
      }
    },
    []
  );

  // Consent-gated geolocation: only fired on the explicit button press. Success →
  // coords (the body `geo`); denial/unavailable → 'denied' (acknowledged, does
  // NOT block — GPS is exceeds-standard, ADISA §3.2.43 mandates only the signed
  // chain-of-custody, not coordinates).
  const onCaptureGps = useCallback(() => {
    // getPosition resolves-only ('denied' on any failure — native catches, web
    // resolves via the error callback). The .catch is defense-in-depth: any
    // unexpected rejection still degrades to the non-blocking 'denied' state.
    getPosition()
      .then(r => setGpsState(r === 'denied' ? 'denied' : r))
      .catch(() => setGpsState('denied'));
  }, []);

  const declared = useMemo(
    () => (detail ? asNumber(detail.order.data.declared_weight) : null),
    [detail]
  );

  const onComplete = useCallback(() => {
    if (!id || submitting) {
      return;
    }
    // Tactile confirmation that the commit was registered (native no-op on web).
    tapHaptic();
    // The geo-omit / reason-only / net-weight invariants live in the shared pure
    // helper (DRY) — the same one the Vitest seam exercises. `client_op_id` is a
    // fresh UUID per attempt (crypto.randomUUID()).
    const body = buildPickupCompleteBody({
      clientOpId: crypto.randomUUID(),
      releasingSig,
      agentSig,
      logisticsSig,
      agentEmployeeId,
      netWeight,
      declaredWeight: declared,
      overrideReason,
      gpsState,
    });

    setSubmitting(true);
    setSubmitError(null);
    // Route through the offline outbox: 'ok' (online, flushed) and 'queued'
    // (offline — persisted on-device, will replay on reconnect) both mean custody
    // is safely captured, so we navigate home (the pending-sync badge reflects any
    // still-queued op). 'error' means an online flush failed; the op STAYS queued
    // + flagged (never dropped) — surface it and let the driver retry.
    outbox
      .submit(id, body)
      .then(outcome => {
        if (outcome === 'error') {
          setSubmitError('com.affine.pipe.driver.exec.submit-error');
          setSubmitting(false);
          return;
        }
        navigate('/');
      })
      .catch((e: unknown) => {
        setSubmitError(e instanceof Error ? e.message : String(e));
        setSubmitting(false);
      });
  }, [
    id,
    submitting,
    netWeight,
    declared,
    releasingSig,
    agentSig,
    logisticsSig,
    agentEmployeeId,
    overrideReason,
    gpsState,
    outbox,
    navigate,
  ]);

  return (
    <PickupExecView
      detail={detail}
      loading={loading}
      error={error}
      onBack={() => navigate('/')}
      releasingSig={releasingSig}
      agentSig={agentSig}
      logisticsSig={logisticsSig}
      onSig={onSig}
      agentEmployeeId={agentEmployeeId}
      onAgentEmployeeId={setAgentEmployeeId}
      netWeight={netWeight}
      onNetWeight={setNetWeight}
      overrideReason={overrideReason}
      onOverrideReason={setOverrideReason}
      gpsState={gpsState}
      onCaptureGps={onCaptureGps}
      onComplete={onComplete}
      submitting={submitting}
      submitError={submitError}
    />
  );
}
